/**
 * Conditional narratives (D156) — one authored ANSWER that knows what the
 * world is doing.
 *
 * @remarks
 * D55 made a description conditional; D74 made every authored line's TEXT
 * conditional. Neither reaches the case this exists for: an answer whose
 * SHAPE changes with the world — a different number of lines, or a different
 * channel. "You are holding the answer to that door" is her voice arriving
 * where the room alone spoke before; conditional text can switch the words
 * inside a line and cannot add her.
 *
 * The shape is D55's, deliberately and for D55's reasons: a plain array (the
 * overwhelming common case, no ceremony) or a `fallback` plus ORDERED
 * `variants`, first match wins. A flat list whose last entry happens to carry
 * no condition is the more familiar idiom and can express two mistakes this
 * cannot — a list with no unconditional arm, and entries after the
 * unconditional one that nothing can reach. Both would need a validator; here
 * they are unrepresentable, which engineering_standards §2.1 prefers and
 * which costs one key.
 *
 * The variant is {@link spokenWhenSchema} — D138's "a condition, and what is
 * said when it holds" — rather than a new pair. Its `when` is REQUIRED, and
 * that is what makes a second fallback impossible to write.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { type SpokenWhen, spokenWhenSchema } from './spoken-when.js';
import { type NarrativeLine, narrativeLinesSchema } from './transcript.js';

/**
 * An authored answer that may vary with world state — see the module remarks.
 *
 * @remarks
 * Hand-written and pinned onto the schema below rather than inferred, for
 * `conditionSchema`'s reason one level up: an authored line's own `text` is
 * already a union, a variant nests lines inside a condition, and asking
 * TypeScript to infer the whole tree through `ContentPackDefinition` tips it
 * into "type instantiation is excessively deep". Naming the type is the cut
 * point, and it also makes the shape readable without running the inferrer
 * in your head.
 */
export type ConditionalNarratives =
	| Readonly<{ readonly fallback: readonly NarrativeLine[]; readonly variants: readonly SpokenWhen[] }>
	| readonly NarrativeLine[];

/**
 * An authored answer that may vary with world state — see the module remarks.
 *
 * @remarks
 * The plain-array arm is not a legacy shim. Most answers are the same answer
 * whatever the world is doing, and making every author write
 * `{ "fallback": [...] }` to say so would be ceremony charged to the common
 * case.
 */
export const conditionalNarrativesSchema: z.ZodType<ConditionalNarratives> = z.union([
	narrativeLinesSchema,
	z
		.strictObject({
			fallback: narrativeLinesSchema,
			variants: z.array(spokenWhenSchema).min(1).readonly(),
		})
		.readonly(),
]);
